/**
 * Creates a queue that runs tasks one at a time per key, while allowing different keys to run
 * concurrently.
 *
 * This exists because Collaterate's project PATCH endpoint is guarded by an optimistic lock on the
 * project's `updated_on` column. Two overlapping PATCHes to the same project race that column and
 * throw a StaleStateException, which reaches the shopper as "Error Occurred. Contact support"
 * (TTI-20060). Checkout legitimately needs to save twice in quick succession, so the saves are
 * serialized rather than avoided.
 *
 * A rejected task does not reject or block the tasks queued behind it - each caller still sees its
 * own result or error.
 *
 * @returns {function(*, function(): Promise): Promise} An enqueue function taking a key and a task.
 */

export default function createSerialQueue () {
    const queues = new Map();

    return function enqueue (key, task) {
        const pending = queues.get(key) || Promise.resolve();
        // Swallow the previous outcome so one failed task does not reject the ones behind it.
        const queued = pending.catch(() => null).then(task);

        queues.set(key, queued);

        const release = () => {
            if (queues.get(key) === queued) {
                queues.delete(key);
            }
        };

        queued.then(release, release);

        return queued;
    };
}
